import React, { useState, useEffect } from 'react';
import { Link, useLocation, Outlet } from 'react-router-dom';
import { 
  LayoutDashboard, 
  FileText, 
  Bell, 
  CreditCard, 
  Search,
  HelpCircle,
  Menu,
  X,
  Crown,
  AlertCircle,
  Briefcase,
  Clock,
  Lock
} from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { useAccess } from '../hooks/useAccess';
import logo from '../assets/images/logo.png';

const DashboardLayout = () => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { 
    services, 
    canAccess, 
    isSubscriptionActive, 
    isFreeTierActive,
    planName,
    daysRemaining: subscriptionDaysRemaining,
    freeTierInfo
  } = useAccess();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [indianTime, setIndianTime] = useState('');

  // Update Indian time every second
  useEffect(() => {
    const updateIndianTime = () => {
      const now = new Date();
      const indianTime = now.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true
      });
      setIndianTime(indianTime);
    };
    
    updateIndianTime();
    const interval = setInterval(updateIndianTime, 1000);
    return () => clearInterval(interval);
  }, []);

  // Get user initials for avatar
  const getUserInitials = () => {
    if (!user?.name) return 'U';
    const names = user.name.split(' ');
    if (names.length >= 2) {
      return `${names[0][0]}${names[1][0]}`.toUpperCase();
    }
    return user.name.substring(0, 2).toUpperCase();
  };

  // Get subscription badge info
  const getSubscriptionBadge = () => {
    // Check for active paid subscription first
    if (isSubscriptionActive) {
      return {
        label: planName || 'Active',
        color: 'bg-yellow-100 text-yellow-700',
        icon: <Crown size={12} />,
        daysRemaining: subscriptionDaysRemaining
      };
    }
    
    // Check for active free tier
    if (isFreeTierActive) {
      // Calculate days remaining for free tier
      const expiryDate = freeTierInfo?.expiry_date ? new Date(freeTierInfo.expiry_date) : null;
      const today = new Date();
      const diffDays = expiryDate ? Math.ceil((expiryDate - today) / (1000 * 60 * 60 * 24)) : 0;
      
      return {
        label: 'Free Tier',
        color: 'bg-emerald-100 text-emerald-700',
        icon: <Crown size={12} />,
        daysRemaining: Math.max(0, diffDays)
      };
    }
    
    return {
      label: 'No Active Plan',
      color: 'bg-gray-100 text-gray-600',
      icon: <AlertCircle size={12} />
    };
  };

  const subscriptionBadge = getSubscriptionBadge();

  // Menu items with service mapping for access control
  const mainMenuItems = [
    { name: 'Dashboard', path: '/dashboard', icon: <LayoutDashboard size={20} />, service: 'dashboard' },
    { name: 'Performance Reports', path: '/dashboard/reports', icon: <FileText size={20} />, service: 'performance_report' },
    { name: 'Important Corporate Announcement', path: '/dashboard/corporate-announcement', icon: <Bell size={20} />, service: 'ica' },
    { name: 'LIVE Important Corporate Announcement', path: '/dashboard/live-announcement', icon: <Bell size={20} />, service: 'live_ica' },
    { name: 'Stocks Search', path: '/dashboard/stocks', icon: <Search size={20} />, service: 'stock_selection' },
    { name: 'Index Search', path: '/dashboard/index-search', icon: <FileText size={20} />, service: 'index_selection' },
    { name: 'My Stocks', path: '/dashboard/my-stocks', icon: <Briefcase size={20} />, service: 'stock_selection' },
    { name: 'Subscription', path: '/dashboard/subscription', icon: <CreditCard size={20} />, service: 'subscriptions' },
    { name: 'Notifications', path: '/dashboard/notifications', icon: <Bell size={20} />, service: 'notifications' },
    { name: 'Write a Review', path: '/dashboard/review', icon: <FileText size={20} />, service: 'profile' },
  ];

  const supportMenuItems = [
    { name: 'Help & User Guide', path: '/dashboard/help', icon: <HelpCircle size={20} />, service: null },
  ];

  // Render menu item with access check
  const renderMenuItem = (item) => {
    const hasAccess = item.service ? canAccess(item.service) : true;
    const isActive = location.pathname === item.path;
    
    return (
      <Link
        key={item.path}
        to={item.path}
        onClick={() => setSidebarOpen(false)}
        className={`flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
          isActive
            ? 'bg-yellow-400 text-gray-900 font-semibold'
            : hasAccess
            ? 'text-white hover:bg-white/10'
            : 'text-gray-400 hover:bg-white/5'
        }`}
      >
        <div className="flex items-center gap-3">
          {item.icon}
          <span className="text-sm">{item.name}</span>
        </div>
        {!hasAccess && (
          <Lock size={14} className="text-gray-500" />
        )}
      </Link>
    );
  };

  return (
    <div className="flex h-screen bg-gray-50">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-[#1b2d4a] text-white transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        {/* Logo */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <Link to="/" className="flex items-center gap-3">
            <div className="bg-white rounded-full p-2 w-12 h-12 flex items-center justify-center">
              <img 
                src={logo} 
                alt="Climb Capital Logo" 
                className="w-full h-full object-contain"
              />
            </div>
            <div>
              <div className="font-bold text-sm leading-tight">CLIMB CAPITAL</div>
              <div className="text-xs text-gray-300 leading-tight">Dashboard</div>
            </div>
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden text-white hover:text-gray-300"
          >
            <X size={24} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="p-4 space-y-2 overflow-y-auto h-[calc(100vh-100px)]">
          {/* Main Menu Section */}
          <div className="mb-6">
            <p className="text-xs font-semibold text-gray-400 uppercase mb-2 px-4">Main Menu</p>
            {mainMenuItems.map(renderMenuItem)}
          </div>

          {/* Support Section */}
          <div>
            <p className="text-xs font-semibold text-gray-400 uppercase mb-2 px-4">Support</p>
            {supportMenuItems.map(renderMenuItem)}
          </div>
        </nav>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Top Header */}
        <header className="bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <button
            onClick={() => setSidebarOpen(true)}
            className="lg:hidden text-gray-600 hover:text-gray-900"
          >
            <Menu size={24} />
          </button>
          
          <div className="flex-1 lg:flex-none">
            <h1 className="text-2xl font-bold text-gray-900">
              {[...mainMenuItems, ...supportMenuItems].find(item => item.path === location.pathname)?.name || 'Dashboard'}
            </h1>
          </div>

          <div className="flex items-center gap-4">
            {/* Indian Time Clock */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full text-xs font-semibold text-gray-700">
              <Clock size={14} className="text-blue-600" />
              <span>{indianTime}</span>
              <span className="text-gray-500">IST</span>
            </div>

            {/* Subscription Badge */}
            {subscriptionBadge && (
              <Link 
                to="/dashboard/subscription" 
                className={`hidden md:flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold ${subscriptionBadge.color} hover:opacity-80 transition-opacity`}
              >
                {subscriptionBadge.icon}
                <span>{subscriptionBadge.label}</span>
                {subscriptionBadge.daysRemaining !== undefined && (
                  <span className="text-xs opacity-75">
                    ({subscriptionBadge.daysRemaining}d left)
                  </span>
                )}
              </Link>
            )}
            
            <Link to="/dashboard/profile" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <div className="w-10 h-10 bg-yellow-400 rounded-full flex items-center justify-center font-bold text-gray-900">
                {getUserInitials()}
              </div>
              <div className="hidden md:block">
                <div className="text-sm font-semibold text-gray-900">{user?.name || 'User'}</div>
                <div className="text-xs text-gray-500">
                  {isSubscriptionActive 
                    ? planName 
                    : isFreeTierActive
                    ? 'Free Tier User'
                    : 'Free User'}
                </div>
              </div>
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 overflow-y-auto p-6">
          <Outlet />
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default DashboardLayout;
